/**
 * Moving an object's pivot without moving its picture (context/future/pivot.md §Pivot shift preserving visual position).
 *
 * The pivot is where `x`/`y` sit and what rotation turns about. Moving it to a point the object already renders at needs two writes: the new pivot, and `x`/`y` = that point, since the new pivot already renders there. Nothing else moves.
 */
import { localExtent, localToParent, objectPosition, parentToLocal } from '~/lib/objectBounds'
import { quantize } from '~/lib/params'
import { objectProps } from '~/lib/presetSettings'

/** A point as the store will hold it as `x`/`y`: rounded to their contract `step`. */
const storable = (type, p) => {
	const props = objectProps(type)
	return { x: quantize(props?.x, p.x), y: quantize(props?.y, p.y) }
}

/** The patches that put `obj`'s pivot on `point` (parent frame) without moving a pixel; ⚠️ derived from the rounded position the store keeps, or the picture moves by that rounding. */
export function buildPivotUpdates(obj, map, point) {
	if (obj.type === 'group') {
		const local = parentToLocal(obj, map, point)
		if (!local) return []
		const shift = storable(obj.type, local)
		if (!shift.x && !shift.y) return []
		const at = localToParent(obj, map, shift)
		const children = (obj.children ?? []).map(id => map[id]).filter(Boolean)
		return [
			{ id: obj.id, x: at.x, y: at.y },
			...children.map(c => {
				const pos = objectPosition(c)
				return { id: c.id, x: pos.x - shift.x, y: pos.y - shift.y }
			}),
		]
	}

	const at = storable(obj.type, point)
	const local = parentToLocal(obj, map, at)
	if (!local) return []
	const e = localExtent(obj, map)
	if (!(e.width > 0) || !(e.height > 0)) return []
	const pivotX = (local.x - e.x) / e.width
	const pivotY = (local.y - e.y) / e.height
	return [{ id: obj.id, pivotX, pivotY, x: at.x, y: at.y }]
}
